class Route {
  constructor(path, hasHeader, hasTools, isOnboarding) {
    this.path = path;
    this.hasHeader = hasHeader;
    this.hasTools = hasTools;
    this.isOnboarding = isOnboarding;
  }

  equals = path => {
    const path1 = path;
    const path2 = this.path;
    if (!path1 || !path2) {
      return false;
    }
    const path1Parts = path1.split("/");
    const path2Parts = path2.split("/");

    if (path1Parts.length !== path2Parts.length && path2.indexOf("?") === -1) {
      return false;
    } else {
      if (path2.indexOf("?") > -1) {
        for (let i = 0; i < path2Parts.length; i++) {
          if (path2Parts[i].startsWith(":") && path2Parts[i].endsWith("?")) {
            // The corresponding part is optional.
            path1Parts[i] = "optional";
          }
        }
      }
      for (let i = 0; i < path1Parts.length; i++) {
        // Route params start with ':', we don't need to compare them.
        if (path1Parts[i].startsWith(":") || path2Parts[i].startsWith(":")) {
          continue;
        } else {
          if (path1Parts[i] !== path2Parts[i]) {
            return false;
          }
        }
      }
      return true;
    }
  };
}

class RouteWithHeader extends Route {
  constructor(path, hasTools) {
    super(path, true, hasTools);
  }
}

class RouteWithHeaderTools extends RouteWithHeader {
  constructor(path) {
    super(path, true);
  }
}

class RouteWithoutHeaderTools extends Route {
  constructor(path, isOnboarding) {
    super(path, false, false, isOnboarding);
  }
}

class AdminRoute extends RouteWithHeader {
  constructor(path) {
    super(path, false);
  }

  getDashboardPath() {
    return this.path.startsWith("/admin") ? this.path.split("/admin")?.[1] : this.path;
  }
}

class CustomerServiceRoute extends RouteWithHeader {
  constructor(path) {
    super(path, false);
  }
}

export default class TalaeraRoutes {
  /* ----------- Common routes (start) --------------- */
  static ROOT = new RouteWithHeader("/", false /* not always */);
  static CHANGE_PASSWORD = new RouteWithHeader("/change_password", false);
  static SIGN_OUT = new RouteWithoutHeaderTools("/sign_out", true);
  static ERROR = new RouteWithHeaderTools("/error");
  /* ----------- Common routes (end) ----------------- */

  /* ----------- Admin specific routes (start) ------- */
  static ADMIN_ROOT = new AdminRoute("/admin/*");
  static ADMIN_USER_LIST = new AdminRoute("/admin/users");
  static ADMIN_USER_DETAILS = new AdminRoute("/admin/users/:id");
  static ADMIN_COMPANY_LIST = new AdminRoute("/admin/companies");
  static ADMIN_COMPANY_DETAILS = new AdminRoute("/admin/companies/:id");
  static ADMIN_COMPANY_DASHBOARD = new AdminRoute(
    "/admin/companies/:companyId/dashboard"
  );
  static ADMIN_COMPANY_TEAM_DETAILS = new AdminRoute(
    "/admin/companies/:companyId/teams/:id"
  );
  static ADMIN_TEAM_LIST = new AdminRoute("/admin/teams");
  static ADMIN_TEAM_DETAILS = new AdminRoute("/admin/teams/:id");
  static ADMIN_COMPANY_USER_DETAILS = new AdminRoute(
    "/admin/companies/:companyId/users/:id"
  );
  static ADMIN_SCHOOL_LIST = new AdminRoute("/admin/schools");
  static ADMIN_SCHOOL_DETAILS = new AdminRoute("/admin/schools/:id");
  static ADMIN_CALENDAR = new AdminRoute("/admin/calendar");
  static ADMIN_MATCHING = new AdminRoute("/admin/matching");
  static ADMIN_CAPACITY = new AdminRoute("/admin/capacity");
  static ADMIN_SESSION_LIST = new AdminRoute("/admin/sessions");
  static ADMIN_SESSION_DETAILS = new AdminRoute("/admin/sessions/:id");
  static ADMIN_QUESTION_LIST = new AdminRoute("/admin/questions");
  static ADMIN_QUESTION_DETAILS = new AdminRoute("/admin/questions/:id");
  static ADMIN_ENGAGEMENT = new AdminRoute("/admin/engagement");
  static ADMIN_MONITORING_SHIFT = new AdminRoute("/admin/monitoring/shift");
  static ADMIN_SETTINGS = new AdminRoute("/admin/settings");
  static ADMIN_PLAN_LIST = new AdminRoute("/admin/plans");
  static ADMIN_EXERCISE_LIST = new AdminRoute("/admin/exercises");
  static ADMIN_EXERCISE_DETAILS = new AdminRoute("/admin/exercises/:id");
  static ADMIN_EXERCISE_BUILDER = new AdminRoute("/admin/builder/exercises");
  static ADMIN_TAG_LIST = new AdminRoute("/admin/tags");
  static ADMIN_TAG_DETAILS = new AdminRoute("/admin/tags/:id");
  static ADMIN_CONTENT_FOLDER = new RouteWithHeaderTools("/admin/content");
  static ADMIN_CREATE_CONTENT_FILE = new RouteWithHeaderTools(
    "/admin/content/object/create"
  );
  static ADMIN_EDIT_CONTENT_FILE = new RouteWithHeaderTools("/admin/content/edit");
  static ADMIN_IMPORT_CONTENT_FILE = new RouteWithHeaderTools(
    "/admin/content/object/import"
  );
  static ADMIN_PRICING_MODEL_LIST = new AdminRoute("/admin/pricing");
  static ADMIN_PRICING_MODEL_DETAILS = new AdminRoute("/admin/pricing/:id");
  static ADMIN_WORKSHOP_LIST = new AdminRoute("/admin/workshops");
  static ADMIN_WORKSHOP_DETAILS = new AdminRoute("/admin/workshops/:id");
  /* ----------- Admin specific routes (end) --------- */

  /* ----------- Customer Service specific routes (start) ------- */
  static CUSTOMERSERVICE_ROOT = new CustomerServiceRoute("/customerservice");
  static CUSTOMERSERVICE_SESSION_LIST = new CustomerServiceRoute(
    "/customerservice/sessions"
  );
  static CUSTOMERSERVICE_SESSION_DETAILS = new CustomerServiceRoute(
    "/customerservice/sessions/:id"
  );
  /* ----------- Customer Service specific routes (end) ------- */

  /* ----------- Other roles routes (start) ---------- */
  static MY_DASHBOARD = new RouteWithHeaderTools("/sessions");
  static LEVEL_TEST_SCORE = new RouteWithHeaderTools("/test_score");
  static MY_PROGRESS = new RouteWithHeaderTools("/my_progress");
  static EXERCISES = new RouteWithHeaderTools("/exercises");
  static EXERCISES_BY_CREATOR = new RouteWithHeaderTools("/exercises/by/:creatorId");
  static EXERCISE_CREATE = new RouteWithHeaderTools("/exercise/create");
  static EXERCISE_EDIT = new RouteWithHeaderTools("/exercise/:id/edit");
  static EXERCISE_DETAILS = new RouteWithHeaderTools("/exercise/:id");
  static EXERCISE_ASSIGNMENTS = new RouteWithHeaderTools("/exercise/assignments");
  static EXERCISE_ASSIGNMENT_DETAILS = new RouteWithHeaderTools(
    "/exercise/assignments/:id"
  );
  static SPEAKING_CLUB = new RouteWithHeaderTools("/speaking_club");
  static SESSION_FEEDBACK = new RouteWithHeaderTools("/feedback/:id");
  static SESSION_FEEDBACK_EDIT = new RouteWithHeaderTools("/feedback/:id/edit");
  static SETTINGS = new RouteWithHeaderTools("/settings");
  static HOMEWORK = new RouteWithHeaderTools("/homework/:filter?");
  static HOMEWORK_SEARCH = new RouteWithHeaderTools("/homework/search");
  static COURSE = new RouteWithHeaderTools("/course");
  static COURSE_OBJECT = new RouteWithHeaderTools("/course/:id");
  static SEARCH = new RouteWithHeaderTools("/search");
  static STUDENTS = new RouteWithHeaderTools("/students");
  static STUDENT_DETAILS = new RouteWithHeaderTools("/student/:id");
  static INSTRUCTOR_DETAILS = new RouteWithHeaderTools("/instructor/:id");
  static SESSION_CONVERSATION = new RouteWithoutHeaderTools("/conversation/:lessonId");
  static POST_FEEDBACK = new RouteWithoutHeaderTools(
    "/conversation/:lessonId/post_feedback"
  );
  static LIBRARY_ROOT = new RouteWithHeaderTools("/library");
  static LIBRARY_OBJECT = new RouteWithHeaderTools("/library/:id");
  static TEAM_DASHBOARD = new RouteWithHeaderTools("/teams");
  static COMPANY_REPORTS_PAGE = new RouteWithHeaderTools("/company/:company_id/reports");
  static TEAM_DASHBOARD_PARAMS = new RouteWithHeaderTools("/teams/:id");
  static TEAM_MEMBER_DETAILS = new RouteWithHeaderTools("/teams/:id/member/:member_id");
  static TAG_SEARCH = new RouteWithHeaderTools("/tag/:id");
  static LEVEL_TEST = new RouteWithHeaderTools("/level-test");
  /* ----------- Other roles routes (end) ------------ */

  /* ----------- Unauthenticated routes (start) ------ */
  static NOT_FOUND = new RouteWithoutHeaderTools("/not-found", true);
  static RESET_DONE = new RouteWithoutHeaderTools("/reset_done", true);
  static SIGN_IN = new RouteWithoutHeaderTools("/sign_in", true);
  static SIGN_IN_SSO = new RouteWithoutHeaderTools("/sign_in/sso", true);
  static SIGN_IN_OAUTH = new RouteWithoutHeaderTools(
    "/sign_in/oauth/:provider/:step",
    true
  );
  static FORGOT = new RouteWithoutHeaderTools("/forgot", true);
  static PASSWORD_VERIFY = new RouteWithoutHeaderTools("/passwd/verify", true);

  // Routes for account verification. TODO: If UI is the same - merge them.
  static VERIFY = new RouteWithoutHeaderTools("/verify", true); // Verification for pricing model signup.
  static SELF_SIGNUP_VERIFY_INDIVIDUAL = new RouteWithoutHeaderTools(
    "/verify/individual",
    true
  ); // Verification for self sign up.
  static INVITE_VERIFY = new RouteWithoutHeaderTools("/invite/verify", true); // Verification for invite flows.
  // Routes for self sign up.
  static SELF_SIGN_UP = new RouteWithoutHeaderTools("/self_sign_up", true);
  // Routes for pricing model signup.
  static SIGN_UP_WITH_CODE = new RouteWithoutHeaderTools("/sign_up/code/:code", true);
  static SIGN_UP_SETUP_WITH_CODE = new RouteWithoutHeaderTools(
    "/sign_up/setup/code/:code",
    true
  );
  /* ----------- Unauthenticated routes (end) -------- */

  static ALL_ROUTES = (() => {
    const routes = [];
    Object.keys(TalaeraRoutes).forEach(key => {
      if (TalaeraRoutes[key] instanceof Route) {
        routes.push(TalaeraRoutes[key]);
      }
    });
    return routes;
  })();

  static findRouteByPath = path => {
    if (!path) {
      return null;
    }
    for (let i = 0; i < TalaeraRoutes.ALL_ROUTES.length; i++) {
      if (TalaeraRoutes.ALL_ROUTES[i].equals(path)) {
        return TalaeraRoutes.ALL_ROUTES[i];
      }
    }
    return null;
  };

  static isRoot = path => {
    return TalaeraRoutes.ROOT.path === path;
  };

  static isStudentDetailsPage = path => {
    return TalaeraRoutes.STUDENT_DETAILS.equals(path);
  };

  static isStudentsPage = path => {
    return TalaeraRoutes.STUDENTS.equals(path);
  };

  static isExerciseListPage = path => {
    return TalaeraRoutes.EXERCISES.equals(path);
  };

  static isHomeworkPage = path => {
    return TalaeraRoutes.HOMEWORK.equals(path);
  };

  static isOnboardingPage = path => {
    const route = TalaeraRoutes.findRouteByPath(path);
    if (!route) {
      return false;
    }
    return route.isOnboarding;
  };

  static isMyDashboardPage = (path, isUserStudentOrTeacher) => {
    return (
      TalaeraRoutes.MY_DASHBOARD.equals(path) ||
      (TalaeraRoutes.isRoot(path) && isUserStudentOrTeacher)
    );
  };

  static isSearchPage = path => {
    return TalaeraRoutes.SEARCH.equals(path);
  };

  static isAdminUserListOrUserDetailsPage = path => {
    return (
      TalaeraRoutes.ADMIN_USER_LIST.equals(path) ||
      TalaeraRoutes.ADMIN_USER_DETAILS.equals(path)
    );
  };

  static isLibraryPage = path => {
    return TalaeraRoutes.LIBRARY_OBJECT.equals(path);
  };

  static isExerciseDetailsPage = path => {
    return (
      !TalaeraRoutes.EXERCISE_CREATE.equals(path) &&
      TalaeraRoutes.EXERCISE_DETAILS.equals(path)
    );
  };

  static isExerciseEditorPage = path => {
    return TalaeraRoutes.EXERCISE_EDIT.equals(path);
  };

  static isExerciseCreatorPage = path => {
    return TalaeraRoutes.EXERCISE_CREATE.equals(path);
  };

  static isPageWithLanguageTool = (path, isUserStudentOrTeacher) => {
    if (TalaeraRoutes.isRoot(path)) {
      // Root path has all roles but only student and teacher should see Tools nav item
      // and be able to use 'Language Tools' sidebar.
      return isUserStudentOrTeacher;
    } else {
      const route = TalaeraRoutes.findRouteByPath(path);
      return route ? route.hasTools : false;
    }
  };

  static isPageWithHeader = path => {
    const route = TalaeraRoutes.findRouteByPath(path);
    return TalaeraRoutes.isRoot(path) || route ? route.hasHeader : false;
  };

  static comparePathesWithFilter = (location, pathBuilder, id, isExact) => {
    if (!location.pathname || !location.search) {
      return false;
    }
    if (isExact) {
      return location.pathname + location.search === pathBuilder(id);
    } else {
      const currentPath = location.pathname + location.search;
      const expectedPath = pathBuilder(id);
      return (
        currentPath.substring(0, currentPath.indexOf("=")) ===
        expectedPath.substring(0, expectedPath.indexOf("="))
      );
    }
  };

  static isExerciseListFilteredByLevelPath = (location, levelId, isExact) => {
    return TalaeraRoutes.comparePathesWithFilter(
      location,
      TalaeraRoutes.buildExerciseListFilteredByLevelPath,
      levelId,
      isExact
    );
  };

  static isExerciseListFilteredByTagPath = (location, tagId, isExact) => {
    return TalaeraRoutes.comparePathesWithFilter(
      location,
      TalaeraRoutes.buildExerciseListFilteredByTagPath,
      tagId,
      isExact
    );
  };

  static isExerciseListFilteredBySourcePath = (location, sourceId, isExact) => {
    return TalaeraRoutes.comparePathesWithFilter(
      location,
      TalaeraRoutes.buildExerciseListFilteredBySourcePath,
      sourceId,
      isExact
    );
  };

  static isMyExerciseListPath = location => {
    if (!location.pathname) {
      return false;
    }
    return location.pathname.startsWith("/exercises/by/"); //TalaeraRoutes.EXERCISES_BY_CREATOR.path);
  };

  static isHomeworkFilteredBySessionPath = (location, sessionId, isExact) => {
    return TalaeraRoutes.comparePathesWithFilter(
      location,
      TalaeraRoutes.buildHomeworkFilteredBySessionPath,
      sessionId,
      isExact
    );
  };

  static buildAdminWorkshopDetailsPath = workshopId => {
    return `${TalaeraRoutes.ADMIN_WORKSHOP_LIST.path}/${workshopId}`;
  };
  static buildAdminCompanyDetailsPath = companyId => {
    return `${TalaeraRoutes.ADMIN_COMPANY_LIST.path}/${companyId}`;
  };
  static buildAdminCompanyDashboardPath = companyId => {
    return `/admin/companies/${companyId}/dashboard`;
  };
  static buildAdminCompanyTeamDetailsPath = (companyId, teamId) => {
    return `/admin/companies/${companyId}/teams/${teamId}`;
  };
  static buildAdminCompanyUserDetailsPath = (companyId, userId) => {
    return `/admin/companies/${companyId}/users/${userId}`;
  };
  static buildAdminTeamDetailsPath = teamId => {
    return `${TalaeraRoutes.ADMIN_TEAM_LIST.path}/${teamId}`;
  };
  static buildAdminUserDetailsPath = userId => {
    return `${TalaeraRoutes.ADMIN_USER_LIST.path}/${userId}`;
  };
  static buildAdminSessionDetailsPath = sessionId => {
    return `${TalaeraRoutes.ADMIN_SESSION_LIST.path}/${sessionId}`;
  };
  static buildAdminPlanDetailsPath = planId => {
    return `${TalaeraRoutes.ADMIN_PLAN_LIST.path}/${planId}`;
  };
  static buildAdminQuestionDetailsPath = questionId => {
    return `${TalaeraRoutes.ADMIN_QUESTION_LIST.path}/${questionId}`;
  };
  static buildAdminSchoolDetailsPath = schoolId => {
    return `${TalaeraRoutes.ADMIN_SCHOOL_LIST.path}/${schoolId}`;
  };
  static buildAdminExerciseDetailsPath = exerciseId => {
    return `${TalaeraRoutes.ADMIN_EXERCISE_LIST.path}/${exerciseId}`;
  };
  static buildAdminTagDetailsPath = id => {
    return `${TalaeraRoutes.ADMIN_TAG_LIST.path}/${id}`;
  };
  static buildAdminPricingModelDetailsPath = id => {
    return `${TalaeraRoutes.ADMIN_PRICING_MODEL_LIST.path}/${id}`;
  };
  static buildAdminEngagementPath = queryString => {
    return `${TalaeraRoutes.ADMIN_ENGAGEMENT.path}?${queryString}`;
  };
  static buildAdminCreateContentFilePath = libraryObjectId => {
    return `${TalaeraRoutes.ADMIN_CREATE_CONTENT_FILE.path}${libraryObjectId ? "?libraryObjectId=" : ""}${encodeURIComponent(libraryObjectId)}`;
  };
  static buildAdminEditContentFilePath = versionId => {
    return `${TalaeraRoutes.ADMIN_EDIT_CONTENT_FILE.path}${versionId ? "?versionId=" : ""}${encodeURIComponent(versionId)}`;
  };
  static buildAdminImportContentFilePath = key => {
    return `${TalaeraRoutes.ADMIN_IMPORT_CONTENT_FILE.path}${key ? "?key=" : ""}${encodeURIComponent(key)}`;
  };
  static buildAdminContentFolderPath = key => {
    return `${TalaeraRoutes.ADMIN_CONTENT_FOLDER.path}${key ? "?key=" : ""}${encodeURIComponent(key)}`;
  };
  static buildAdminContentLibraryObjectPath = id => {
    return `${TalaeraRoutes.ADMIN_CONTENT_FOLDER.path}${id ? "?library_object_id=" : ""}${encodeURIComponent(id)}`;
  };
  static buildAdminContentLibraryVersionPath = id => {
    return `${TalaeraRoutes.ADMIN_CONTENT_FOLDER.path}${id ? "?library_version_id=" : ""}${encodeURIComponent(id)}`;
  };
  static buildAdminContentLibraryPath = () => {
    return TalaeraRoutes.ADMIN_CONTENT_FOLDER.path;
  };
  static buildExerciseFolderPath = (creatorId, key) => {
    return `/exercises/by/${creatorId}${key ? "?key=" : ""}${encodeURIComponent(key)}`;
  };
  static buildCustomerServiceSessionDetailsPath = sessionId => {
    return `${TalaeraRoutes.CUSTOMERSERVICE_SESSION_LIST.path}/${sessionId}`;
  };

  static buildHomeworkSearchPath = query => {
    let url = `${TalaeraRoutes.HOMEWORK_SEARCH.path}?query=${encodeURIComponent(query)}`;
    return url;
  };

  static buildCoursePath = () => {
    return TalaeraRoutes.COURSE.path;
  };

  static buildCourseObjectPath = (objectId, query = "") => {
    return `/course/${objectId}${query}`;
  };

  static buildLibrarySearchPath = query => {
    let url = `${TalaeraRoutes.LIBRARY_ROOT.path}?query=${encodeURIComponent(query)}`;
    return url;
  };

  static buildLibraryRootPath = () => {
    return TalaeraRoutes.LIBRARY_ROOT.path;
  };
  static buildLibraryObjectPath = (objectId, sectionId = "", query = "") => {
    return `/library/${objectId}${sectionId ? `#${sectionId}` : ""}${query?.sl ?? ""}`;
  };
  static buildStudentsPath = () => {
    return TalaeraRoutes.STUDENTS.path;
  };
  static buildStudentDetailsPath = userId => {
    return `/student/${userId}`;
  };
  static buildInstructorDetailsPath = userId => {
    return `/instructor/${userId}`;
  };
  static buildTeamMemberDetailsPath = (teamId, memberId) => {
    return `/teams/${teamId}/member/${memberId}`;
  };
  static buildTagPath = tagId => {
    return `/tag/${tagId}`;
  };
  static buildPostFeedbackPath = lessonId => {
    return `/conversation/${lessonId}/post_feedback`;
  };
  static buildSessionFeedbackPath = lessonId => {
    return `/feedback/${lessonId}`;
  };
  static buildSessionFeedbackEditPath = lessonId => {
    return `/feedback/${lessonId}/edit`;
  };
  static buildExerciseListPath = queryString => {
    if (queryString) {
      return `/exercises?${queryString}`;
    } else {
      return "/exercises";
    }
  };
  static buildExerciseListFilteredByLevelPath = levelId => {
    return TalaeraRoutes.buildExerciseListPath(`level_id=${levelId}`);
  };
  static buildExerciseListFilteredByTagPath = tagId => {
    return TalaeraRoutes.buildExerciseListPath(`tag_id=${tagId}`);
  };
  static buildExerciseListFilteredBySourcePath = sourceId => {
    return TalaeraRoutes.buildExerciseListPath(`source_id=${sourceId}`);
  };
  static buildMyExerciseListPath = creatorId => {
    return `/exercises/by/${creatorId}`;
  };
  static buildMyWorkSheetListPath = creatorId => {
    return `/ws/by/${creatorId}`;
  };
  static buildExerciseDetailsPath = exerciseId => {
    return `/exercise/${exerciseId}`;
  };
  static buildExerciseCreatePath = key => {
    return `${TalaeraRoutes.EXERCISE_CREATE.path}?key=${key}`;
  };
  static buildExerciseEditPath = exerciseId => {
    return `/exercise/${exerciseId}/edit`;
  };
  static buildExerciseAssignmentDetailsPath = exerciseAssignmentId => {
    return `/exercise/assignments/${exerciseAssignmentId}`;
  };

  static buildCompanyReportsPath = companyId => {
    return `/company/${companyId}/reports`;
  };

  static buildHomeworkPath = filter => {
    if (filter) {
      return `/homework/${filter}`;
    } else {
      return "/homework";
    }
  };
  static buildHomeworkFilteredBySessionPath = sessionId => {
    return `/homework?session_id=${sessionId}`;
  };
  static buildPlanDetailsPath = () => {
    return `${TalaeraRoutes.SETTINGS.path}#plan-details`;
  };

  static buildVerifySignupIndividual = token => {
    return `${TalaeraRoutes.SELF_SIGNUP_VERIFY_INDIVIDUAL.path}?token=${token}`;
  };

  static buildTestLevelPath = () => {
    return TalaeraRoutes.LEVEL_TEST.path;
  };

  static buildSignInOAuthPath = (provider, step, args) => {
    const path = `/sign_in/oauth/${provider}/${step}`;
    const params = new URLSearchParams();
    if (args?.isPro) {
      params.append("is_pro", "true");
    }
    if (args?.code) {
      params.append("code", args.code);
    }
    const queryString = params.toString();
    return queryString ? `${path}?${queryString}` : path;
  };
}
